/**
 * Users Config calls for NON-admin accounts. Every call goes to our server,
 * which checks the `users` tab and forwards to et-auth with the service
 * token. The browser never talks to et-auth's admin plugin.
 */

import { readEtAuthError } from "../../lib/et-auth-errors";

export class UsersConfigRequestError extends Error {
  constructor(message: string, readonly validationErrors: string[] = []) {
    super(message);
  }
}

export const bulkUserServerErrors = (error: unknown, rowNumbers?: number[]): string[] =>
  (error instanceof UsersConfigRequestError && error.validationErrors.length
    ? error.validationErrors
    : [error instanceof Error ? error.message : "Failed to import users"])
    .map((message) => message.replace(/^Row (\d+):/, (prefix, row: string) =>
      rowNumbers?.[Number(row) - 1] === undefined ? prefix : `Row ${rowNumbers[Number(row) - 1]}:`));

const jsonHeaders = { "Content-Type": "application/json" };

const request = async <T>(
  path: string,
  init: RequestInit,
  fallback: string,
): Promise<T> => {
  const res = await fetch(path, { credentials: "include", ...init });
  if (!res.ok) {
    const payload = await res.clone().json().catch(() => null) as { errors?: unknown } | null;
    const validationErrors = Array.isArray(payload?.errors)
      ? payload.errors.flatMap((error) => {
          if (!error || typeof error !== "object") return [];
          const item = error as { row?: unknown; message?: unknown };
          return typeof item.row === "number" && Number.isInteger(item.row) && item.row > 0 && typeof item.message === "string"
            ? [`Row ${item.row}: ${item.message}`]
            : [];
        })
      : [];
    throw new UsersConfigRequestError(await readEtAuthError(res, fallback), validationErrors);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
};

export type CreateUserInput = {
  email: string;
  password: string;
  name: string;
  username?: string;
  displayUsername?: string;
  signupRole?: "STUDENT" | "TEACHER";
  organization?: string;
  organizationId?: string;
  grade?: string;
  classroom?: string;
};

export const usersConfigApi = {
  listUsers: (query: { limit: number; offset: number }) =>
    request<{ users: import("./types").ManagedUser[]; total: number }>(
      `/api/internal/admin/users?limit=${query.limit}&offset=${query.offset}`,
      { method: "GET" },
      "Failed to load users",
    ),

  // Shaped like the old better-auth client response so role-operations can
  // read the created id the same way.
  createUser: async (input: CreateUserInput) => {
    try {
      const data = await request<{ userId: number }>(
        "/api/internal/admin/users",
        { method: "POST", headers: jsonHeaders, body: JSON.stringify(input) },
        "Failed to create user",
      );
      return { data: { id: String(data.userId) }, error: null };
    } catch (err) {
      return { data: undefined, error: { message: err instanceof Error ? err.message : "Failed to create user" } };
    }
  },

  bulkCreateUsers: (users: CreateUserInput[]) =>
    request<{ results: Array<{ email: string; userId: number; ok: true }> }>(
      "/api/internal/admin/users/bulk", { method: "POST", headers: jsonHeaders, body: JSON.stringify({ users }) },
      "Failed to import users",
    ),

  updateProfile: (
    userId: string,
    data: { name?: string; email?: string; username?: string; displayUsername?: string },
  ) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}/profile`,
      { method: "PATCH", headers: jsonHeaders, body: JSON.stringify(data) },
      "Failed to update user",
    ),

  setPassword: (userId: string, newPassword: string) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}/password`,
      { method: "POST", headers: jsonHeaders, body: JSON.stringify({ newPassword }) },
      "Failed to reset password",
    ),

  banUser: (userId: string, reason?: string) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}/ban`,
      { method: "POST", headers: jsonHeaders, body: JSON.stringify({ reason }) },
      "Failed to ban user",
    ),

  unbanUser: (userId: string) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}/unban`,
      { method: "POST" },
      "Failed to unban user",
    ),

  revokeSessions: (userId: string) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}/revoke-sessions`,
      { method: "POST" },
      "Failed to revoke sessions",
    ),

  deleteUser: (userId: string) =>
    request<{ userId: number }>(
      `/api/internal/admin/users/${userId}`,
      { method: "DELETE" },
      "Failed to delete user",
    ),
};
